"use client";

import { ReactElement } from "react";
import { Trash2 } from "lucide-react";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "./typography";
import { Button } from "./buttons/button";
import { Badge } from "./badge";
import { CheckIcon } from "./icons/check-icon";
import { AlertIcon } from "./icons/alert-icon";

type Props = {
  icon: ReactElement;
  platformName: string;
  isConnected: boolean;
  username?: string;
  additionalInfo?: string;
  loading?: boolean;
  daysRemaining?: number;
  onConnect: () => void;
  onDisconnect?: () => void;
};

/**
 * SocialConnectionCard component for displaying social media platform connection status.
 * Shows connected/disconnected states with appropriate actions and user information.
 */
export const SocialConnectionCard = ({
  icon,
  platformName,
  isConnected,
  username,
  additionalInfo,
  loading = false,
  daysRemaining,
  onConnect,
  onDisconnect,
}: Props) => {
  const isExpiring =
    isConnected && typeof daysRemaining === "number" && daysRemaining < 15;

  return (
    <div
      className={cn(
        "bg-bg-primary border-border-primary flex flex-col gap-3 rounded-lg border-1 p-3 transition-all",
        isConnected && "border-black",
      )}
    >
      {/* Logo & Status */}
      <div className="flex flex-row items-center justify-start gap-1.5">
        {icon} {/* Usually 28x28 */}
        <Typography weight="bold">{platformName}</Typography>
        {/* Connected Chip */}
        {isConnected && !isExpiring && (
          <div className="ml-auto">
            <Badge icon={CheckIcon} label="Connected" variant="lightGreen" />
          </div>
        )}
        {isExpiring && (
          <div className="ml-auto">
            <Badge
              icon={AlertIcon}
              label={daysRemaining === 0 ? "Expired" : "Expiring"}
              variant="error"
            />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-row items-center justify-between">
        {/* Connected state */}
        {isConnected && (
          <>
            {/* Username & Additional Info */}
            <div className="flex flex-row gap-0.5">
              {username && (
                <Typography weight="semibold">@{username}</Typography>
              )}

              {additionalInfo && (
                <Typography color="neutral">
                  {username ? " · " : ""}
                  {additionalInfo}
                </Typography>
              )}
            </div>

            {/* Show Reconnect button if expiring, otherwise show Disconnect */}
            {isExpiring ? (
              <Button
                variant="text"
                loading={loading}
                onClick={onConnect}
                className="text-primary py-0 font-semibold"
                disabled={loading}
              >
                {loading ? "Reconnecting" : "Reconnect"}
              </Button>
            ) : (
              onDisconnect && (
                <Button
                  variant="text"
                  loading={loading}
                  onClick={onDisconnect}
                  size="lg"
                  className="py-0"
                  icon={Trash2}
                  disabled={loading}
                />
              )
            )}
          </>
        )}

        {/* Disconnected state */}
        {!isConnected && (
          <Button
            variant="text"
            loading={loading}
            onClick={onConnect}
            className="text-primary py-0 font-semibold"
            disabled={loading}
          >
            {loading ? "Connecting" : "Connect"}
          </Button>
        )}
      </div>
    </div>
  );
};
